/* eslint-disable camelcase */
import Config from 'app.config';
import { get, pickBy } from 'lodash-es';
import { call, put, takeLatest } from 'redux-saga/effects';
import { yieldErrorHandling } from 'utils/common';
import request, { buildRequestObj } from 'utils/request';

import {
  FAVORITE_THERAPIST,
  FETCH_THERAPISTS,
  FETCH_THERAPIST_DETAIL,
  FETCH_THERAPIST_REVIEWS,
  FETCH_USER_THERAPISTS,
  RATE_THERAPIST,
  TIP_THERAPIST,
  UNFAVORITE_THERAPIST,
  UPDATE_PRIVATE_NOTE,
} from './constants';

import {
  favoriteTherapistFailure,
  favoriteTherapistSuccess,
  fetchTherapistDetailFailure,
  fetchTherapistDetailSuccess,
  fetchTherapistReviewsFailure,
  fetchTherapistReviewsSuccess,
  fetchTherapistsFailure,
  fetchTherapistsSuccess,
  fetchUserTherapistsFailure,
  fetchUserTherapistsSuccess,
  rateTherapistFailure,
  rateTherapistSuccess,
  tipTherapistFailure,
  tipTherapistSuccess,
  unFavoriteTherapistFailure,
  unFavoriteTherapistSuccess,
  updatePrivateNoteFailure,
  updatePrivateNoteSuccess,
} from './actions';

export function* fetchTherapistsSaga(action) {
  const { locationId } = action;
  const reqObj = buildRequestObj('GET', {}, true);
  let reqUrl = `${Config.apiBasev2}/therapists`;
  if (locationId) {
    reqUrl = `${reqUrl}?location_id=${locationId}`;
  }
  try {
    const result = yield call(request, reqUrl, reqObj);
    yield put(fetchTherapistsSuccess(result.items));
  } catch (error) {
    yield yieldErrorHandling(
      error,
      fetchTherapistsFailure,
      'fetch therapists failed',
    );
  }
}

function* watchFetchTherapists() {
  yield takeLatest(FETCH_THERAPISTS, fetchTherapistsSaga);
}

export function* fetchTherapistReviewsSaga(action) {
  const { therapistId, offset, limit, userTherapist } = action;
  const userId = localStorage.getItem('userId');
  const reqObj = buildRequestObj('GET', {}, true);
  let reqUrl = `${Config.apiBasev2}/reviews`;
  if (therapistId) {
    reqUrl = `${reqUrl}?therapist_id=${therapistId}&comment_not_null=true&is_approved=1&offset=${offset ||
      0}&limit=${limit || 25}`;
  }
  if (userTherapist) {
    reqUrl = `${reqUrl}&user_id=${userId}`;
  }
  try {
    const result = yield call(request, reqUrl, reqObj);
    yield put(fetchTherapistReviewsSuccess(result));
  } catch (error) {
    yield yieldErrorHandling(
      error,
      fetchTherapistReviewsFailure,
      'fetch therapist reviews failed',
    );
  }
}

function* watchFetchTherapistReviews() {
  yield takeLatest(FETCH_THERAPIST_REVIEWS, fetchTherapistReviewsSaga);
}

export function* fetchTherapistDetailSaga(action) {
  const { therapistId, userReviewsOnly } = action;
  const reqObj = buildRequestObj('GET', {}, true);
  const reqUrl = userReviewsOnly
    ? `${Config.apiBasev2}/therapists?id=${therapistId}&user_therapist=true`
    : `${Config.apiBasev2}/therapists?id=${therapistId}`;
  // if (userReviewsOnly) {
  //   reqUrl = `${reqUrl}?user_reviews_only=${userReviewsOnly}`;
  // }
  try {
    const result = yield call(request, reqUrl, reqObj);
    const therapistObj = result.items && result.items[0];
    yield put(fetchTherapistDetailSuccess(therapistObj));
  } catch (error) {
    yield yieldErrorHandling(
      error,
      fetchTherapistDetailFailure,
      'fetch therapist detail failed',
    );
  }
}

function* watchFetchTherapistDetail() {
  yield takeLatest(FETCH_THERAPIST_DETAIL, fetchTherapistDetailSaga);
}

export function* tipTherapistSaga(action) {
  const { tipType, amount, appointmentId } = action;
  const reqObj = buildRequestObj(
    'POST',
    pickBy({ type: tipType, amount }),
    true,
  );
  const reqUrl = `${Config.apiBasev2}/appointment/${appointmentId}/tip`;
  try {
    yield call(request, reqUrl, reqObj);
    yield put(tipTherapistSuccess({ success: true }));
  } catch (error) {
    yield yieldErrorHandling(
      error,
      tipTherapistFailure,
      'tip therapist failed',
    );
  }
}

function* watchTipTherapist() {
  yield takeLatest(TIP_THERAPIST, tipTherapistSaga);
}

export function* rateTherapistSaga(action) {
  const {
    therapist_id,
    appointment_id,
    rating,
    favorite,
    share_public,
    share_therapist,
    comment,
    private_note,
  } = action;

  const obj = {
    therapist_id,
    appointment_id,
    share_to_public: share_public,
    share_to_therapist: share_therapist,
    rating,
  };
  if (favorite) {
    obj.favourite = favorite;
  }
  if (comment) {
    obj.comment = comment;
  }
  if (private_note) {
    obj.private_note = private_note;
  }
  const reqObj = buildRequestObj('POST', obj, true);
  const reqUrl = `${Config.apiBasev2}/therapist/review`;
  try {
    const result = yield call(request, reqUrl, reqObj);
    yield put(rateTherapistSuccess({ ...result, success: true }));
  } catch (error) {
    yield yieldErrorHandling(
      error,
      rateTherapistFailure,
      'rating therapist failed',
    );
  }
}

function* watchRateTherapist() {
  yield takeLatest(RATE_THERAPIST, rateTherapistSaga);
}

export function* fetchUserTherapistsSaga() {
  const reqObj = buildRequestObj('GET', {}, true);
  const reqUrl = `${Config.apiBasev2}/therapists?user_therapist=true`;
  try {
    const result = yield call(request, reqUrl, reqObj);
    yield put(fetchUserTherapistsSuccess(get(result, 'items')));
  } catch (error) {
    yield yieldErrorHandling(
      error,
      fetchUserTherapistsFailure,
      'fetch usertherapists failed',
    );
  }
}

function* watchFetchUserTherapists() {
  yield takeLatest(FETCH_USER_THERAPISTS, fetchUserTherapistsSaga);
}

export function* favoriteTherapistSaga(action) {
  const { therapistId } = action;
  const reqObj = buildRequestObj('POST', { therapist_id: therapistId }, true);
  const reqUrl = `${Config.apiBasev2}/user/favourite-therapist`;
  try {
    yield call(request, reqUrl, reqObj);
    yield put(favoriteTherapistSuccess({ success: true }));
  } catch (error) {
    yield yieldErrorHandling(
      error,
      favoriteTherapistFailure,
      'favoriting therapist failed',
    );
  }
}

function* watchFavoriteTherapist() {
  yield takeLatest(FAVORITE_THERAPIST, favoriteTherapistSaga);
}

export function* unfavoriteTherapistSaga(action) {
  const { therapistId } = action;
  const reqObj = buildRequestObj('DELETE', {}, true);
  const reqUrl = `${Config.apiBasev2}/user/favourite-therapist/${therapistId}`;
  try {
    yield call(request, reqUrl, reqObj);
    yield put(unFavoriteTherapistSuccess({ success: true }));
  } catch (error) {
    yield yieldErrorHandling(
      error,
      unFavoriteTherapistFailure,
      'favoriting therapist failed',
    );
  }
}

function* watchUnfavoriteTherapist() {
  yield takeLatest(UNFAVORITE_THERAPIST, unfavoriteTherapistSaga);
}

export function* updatePrivateNoteSaga(action) {
  const { therapist_id, private_note } = action;
  const reqObj = buildRequestObj('PUT', { private_note }, true);
  const reqUrl = `${Config.apiBasev2}/therapist/${therapist_id}/notes`;
  try {
    yield call(request, reqUrl, reqObj);
    yield put(updatePrivateNoteSuccess({ success: true }));
  } catch (error) {
    yield yieldErrorHandling(
      error,
      updatePrivateNoteFailure,
      'updating private note failed',
    );
  }
}

function* watchUpdatePrivateNote() {
  yield takeLatest(UPDATE_PRIVATE_NOTE, updatePrivateNoteSaga);
}

export default {
  watchFetchTherapists,
  watchFetchTherapistReviews,
  watchFetchTherapistDetail,
  watchTipTherapist,
  watchRateTherapist,
  watchFetchUserTherapists,
  watchFavoriteTherapist,
  watchUnfavoriteTherapist,
  watchUpdatePrivateNote,
};
